import type { ReactNode } from "react"
import { Navigate, useParams, useLocation } from "react-router-dom"
import HomeLayout from "modules/home-layout"
import { useAccountAlias, useIsMultiDashboardsEnabled } from "resources/account"
import { useClearDashboardPreferences } from "./hooks/use-clear-dashboard-preferences"
import { DEFAULT_DASHBOARD_ID } from "./context/dashboard-id"
import { CurrentDashboardIdProvider, useCurrentDashboardId } from "./context/current-dashboard-id"
import { useDashboardRouteResolution } from "./hooks/use-dashboard-route-resolution"
import { useInstanceWorkspaceLoader } from "./hooks/use-instance-workspace-loader"
import { useTouchInstanceOnOpen } from "./hooks/use-touch-instance-on-open"
import { InstanceWorkspaceProvider } from "./context/instance-workspace-context"
import { DashboardAiContextProvider } from "./context/dashboard-ai-context-provider"
import { MountedContentProvider } from "./context/mounted-content"

function InstanceWorkspaceScope({ children }: { children: ReactNode }) {
  const dashboardId = useCurrentDashboardId()
  const isInstanceMode = useIsMultiDashboardsEnabled()
  const { currentSeed, loadSucceeded } = useInstanceWorkspaceLoader(dashboardId)
  useTouchInstanceOnOpen(dashboardId, loadSucceeded)

  return (
    <InstanceWorkspaceProvider enabled={isInstanceMode} currentSeed={currentSeed}>
      {children}
    </InstanceWorkspaceProvider>
  )
}

export default function DashboardHomeLayout() {
  const accountAlias = useAccountAlias()
  const { dashboardId: routeDashboardId } = useParams()
  const { search } = useLocation()

  const resolution = useDashboardRouteResolution(routeDashboardId)
  const resolvedDashboardId = resolution.kind === "resolved" ? resolution.dashboardId : null

  // Gate the clear on a RESOLVED id: ungated, ?clearPreferences would race the instances fetch and
  // wipe the DEFAULT-fallback dashboard instead of the routed one.
  const { isClearing: isPreferencesClearing } = useClearDashboardPreferences(
    resolvedDashboardId ?? DEFAULT_DASHBOARD_ID,
    resolvedDashboardId !== null,
  )

  const currentDashboardId = isPreferencesClearing ? null : resolvedDashboardId

  // Unknown / foreign / deleted id: replace-navigate to the account's bare /dashboard (toast fired
  // in the hook; alias prefix required since routes are scoped under /:accountValue). `search`
  // keeps a deep-link instrument on the fallback dashboard. [APP-3328]
  // A non-canonical id (e.g. a raw instanceId) is corrected to its slug once the resolver has
  // confirmed the match; `search` survives the hop. [APP-3328]
  let redirectTo: string | null = null
  if (resolution.kind === "redirect") {
    redirectTo = `/${accountAlias}/dashboard${search}`
  } else if (resolution.kind === "canonicalize") {
    redirectTo = `/${accountAlias}/dashboard/${resolution.slug}${search}`
  }

  return (
    <CurrentDashboardIdProvider value={currentDashboardId}>
      <MountedContentProvider>
        <InstanceWorkspaceScope>
          <DashboardAiContextProvider>
            <HomeLayout />
          </DashboardAiContextProvider>
        </InstanceWorkspaceScope>
      </MountedContentProvider>
      {redirectTo !== null && <Navigate to={redirectTo} replace />}
    </CurrentDashboardIdProvider>
  )
}
